import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  appointments: [],
  filteredAppointments: [],
  displayedAppointments: [],

  // Analytics related state
  appointmentAnalytics: {
    data: [],
    filters: {
      timeRange: 'Today',
      fromDate: '',
      toDate: '',
      staffFilter: ''
    },
    loading: false,
    error: null
  },

  updateCount: {},
  appointmentStatuses: ['RESERVED', 'COMPLETE'],
  appointmentSources: ['Walk In', 'Existing', 'Phone Call', 'Facebook', 'Web Appointment'],
  genders: ['Female', 'Male'],
  updatedAppointmentData: {
    clientName: '',
    clientNumber: '',
    dateOfAppointment: '',
    sourceOfAppointment: '',
    timeOfAppointment: '',
    gender: '',
    serviceSelected: [
      {
        service: '',
        serviceProvider: '',
      },
    ],
  },
  pagination: {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 1,
  },
  filters: {
    search: '',
    gender: [],
    sourceOfAppointment: [],
    appointmentStatus: [],
  }
};

const appointmentSlice = createSlice({
  name: 'Appointments',
  initialState,
  reducers: {
    setAllAppointmentData: (state, action) => {
      state.appointments = action.payload;
      applyFilters(state);
    },

    updateAppointmentInState: (state, action) => {
      const updatedAppointment = action.payload;
      state.appointments = state.appointments.map(appointment =>
        appointment._id === updatedAppointment._id ? updatedAppointment : appointment
      );
      applyFilters(state);
    },

    removeAppointmentsFromState: (state, action) => {
      const idsToRemove = action.payload;
      state.appointments = state.appointments.filter(
        appointment => !idsToRemove.includes(appointment._id)
      );
      applyFilters(state);
    },

    setSearchFilter: (state, action) => {
      state.filters.search = action.payload.toLowerCase();
      applyFilters(state);
    },

    setGenderFilter: (state, action) => {
      state.filters.gender = action.payload;
      applyFilters(state);
    },

    setSourceFilter: (state, action) => {
      state.filters.sourceOfAppointment = action.payload;
      applyFilters(state);
    },

    setStatusFilter: (state, action) => {
      state.filters.appointmentStatus = action.payload;
      applyFilters(state);
    },

    setItemsPerPage: (state, action) => {
      state.pagination.itemsPerPage = action.payload;
      state.pagination.currentPage = 1;
      applyFilters(state);
    },

    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
      updateDisplayedAppointments(state);
    },

    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.itemsPerPage = 10;
      applyFilters(state);
    },

    updateAppointmentData: (state, action) => {
      state.updatedAppointmentData = action.payload;
    },

    clearUpdatedAppointmentData: (state) => {
      state.updatedAppointmentData = initialState.updatedAppointmentData;
    },

    setAppointmentAnalyticsData: (state, action) => {
      state.appointmentAnalytics.data = action.payload;
      state.appointmentAnalytics.loading = false;
    },

    setAppointmentAnalyticsLoading: (state, action) => {
      state.appointmentAnalytics.loading = action.payload;
    },

    setAppointmentAnalyticsError: (state, action) => {
      state.appointmentAnalytics.error = action.payload;
      state.appointmentAnalytics.loading = false;
    },

    setAppointmentAnalyticsFilters: (state, action) => {
      state.appointmentAnalytics.filters = {
        ...state.appointmentAnalytics.filters,
        ...action.payload
      };
    },

    resetAppointmentAnalyticsFilters: (state) => {
      state.appointmentAnalytics.filters = initialState.appointmentAnalytics.filters;
    },
  },
});

// Helper functions
function applyFilters(state) {
  const { search, gender, sourceOfAppointment, appointmentStatus } = state.filters;
  const { appointments } = state;

  const fuzzyMatch = (text, lowerSearch) => {
    const lowerText = text?.toString().toLowerCase().replace(/\s/g, '');
    if (!lowerText) return false;

    let textIndex = 0;
    let searchIndex = 0;
    while (textIndex < lowerText.length && searchIndex < lowerSearch.length) {
      if (lowerText[textIndex] === lowerSearch[searchIndex]) {
        searchIndex++;
      }
      textIndex++;
    }
    return searchIndex === lowerSearch.length;
  };

  const lowerSearch = search.replace(/\s/g, ''); // search is already lowercased in setSearchFilter

  state.filteredAppointments = appointments.filter(item => {
    const matchesSearch = !search ||
      fuzzyMatch(item.clientName, lowerSearch) ||
      fuzzyMatch(item.clientNumber, lowerSearch);

    const matchesGender = gender.length === 0 || gender.includes(item.gender);
    const matchesSource = sourceOfAppointment.length === 0 ||
      sourceOfAppointment.includes(item.sourceOfAppointment);
    const matchesStatus = appointmentStatus.length === 0 ||
      appointmentStatus.includes(item.appointmentStatus);

    return matchesSearch && matchesGender && matchesSource && matchesStatus;
  });

  // Update pagination
  state.pagination.totalItems = state.filteredAppointments.length;
  state.pagination.totalPages = Math.ceil(
    state.filteredAppointments.length / state.pagination.itemsPerPage
  ) || 1;

  if (state.pagination.currentPage > state.pagination.totalPages && state.pagination.totalPages > 0) {
    state.pagination.currentPage = 1;
  }

  updateDisplayedAppointments(state);
}
function updateDisplayedAppointments(state) {
  const { currentPage, itemsPerPage } = state.pagination;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  state.displayedAppointments = state.filteredAppointments.slice(startIndex, endIndex);
}

export const {
  setAllAppointmentData,
  updateAppointmentInState,
  removeAppointmentsFromState,
  setSearchFilter,
  setGenderFilter,
  setSourceFilter,
  setStatusFilter,
  setItemsPerPage,
  setCurrentPage,
  resetFilters,
  updateAppointmentData,
  clearUpdatedAppointmentData,

  // Analytics actions
  setAppointmentAnalyticsData,
  setAppointmentAnalyticsLoading,
  setAppointmentAnalyticsError,
  setAppointmentAnalyticsFilters,
  resetAppointmentAnalyticsFilters
} = appointmentSlice.actions;

export default appointmentSlice.reducer;